// ── API Key Lifecycle ─────────────────────────────────────────────────────────

import type {
  ApiKeyMeta,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  RotateApiKeyResponse,
} from '../../types/apiKeys';
import { apiRequest } from './http';

// Backend entity fields differ from the frontend ApiKeyMeta shape.
// name→keyName, state→status, _createdAt→createdAt
function mapApiKeyEntity(raw: Record<string, unknown>): ApiKeyMeta {
  return {
    keyId: raw.keyId as string,
    keyName: (raw.name ?? raw.keyName) as string,
    scopes: raw.scopes as ApiKeyMeta['scopes'],
    status: (raw.state ?? raw.status) as string as ApiKeyMeta['status'],
    createdAt: (raw._createdAt ?? raw.createdAt) as string,
    expiresAt: raw.expiresAt as string,
    lastUsedAt: (raw.lastUsedAt as string | null) ?? null,
    overlapEndsAt: raw.overlapEndsAt as string | undefined,
  };
}

export async function listApiKeys(): Promise<ApiKeyMeta[]> {
  const raw = await apiRequest<Record<string, unknown>[]>('/api-keys', { method: 'GET' });
  return raw.map(mapApiKeyEntity);
}

export async function createApiKey(input: CreateApiKeyInput): Promise<CreateApiKeyResponse> {
  const expiresInDays = Math.max(
    1,
    Math.round((new Date(input.expiresAt).getTime() - Date.now()) / 86_400_000)
  );
  const raw = await apiRequest<Record<string, unknown>>('/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name: input.keyName, scopes: input.scopes, expiresInDays }),
  });
  return { rawKey: raw.rawKey as string, key: mapApiKeyEntity(raw) };
}

export async function rotateApiKey(keyId: string): Promise<RotateApiKeyResponse> {
  const raw = await apiRequest<{
    rawKey: string;
    newKey: Record<string, unknown>;
    oldKey: Record<string, unknown>;
  }>(`/api-keys/${keyId}/rotate`, { method: 'POST' });
  return { rawKey: raw.rawKey, key: mapApiKeyEntity(raw.newKey) };
}

export async function revokeApiKey(keyId: string): Promise<void> {
  return apiRequest<void>(`/api-keys/${keyId}`, { method: 'DELETE' });
}
